import React, { useEffect, useState } from "react";
import { FaEdit, FaTrashAlt, FaSearch, FaPlus, FaUsers, FaUserCheck, FaUserTimes, FaEye, FaSort, FaSortUp, FaSortDown, FaFilter, FaDownload } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { grid } from "ldrs"; // Importing l-grid
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// Register the grid loader
grid.register();

const UsersTable = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [loggedInUserRole] = useState(localStorage.getItem("role"));
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Store all users for filtering
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]); // Users to display on current page
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editName, setEditName] = useState("");
  const [editUserId, setEditUserId] = useState(null);
  
  // Read URL parameters
  const searchParams = new URLSearchParams(location.search);
  const openModalParam = searchParams.get('openModal');
  const roleParam = searchParams.get('role');
  
  const [showModal, setShowModal] = useState(openModalParam === 'true');
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: loggedInUserRole === "companyAdmin" ? "user" : "companyAdmin",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [loadingAddUser, setLoadingAddUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    companyAdmins: 0,
    regularUsers: 0
  });
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterRole, setFilterRole] = useState(roleParam || 'all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(!!roleParam); // Show filters if role param is provided
  const [selectedUsers, setSelectedUsers] = useState([]);
  const limit = 10;

  // Skeleton Components
  const SkeletonStatsCard = () => (
    <div className="bg-white rounded-lg shadow border border-gray-100 p-3 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded w-12 mb-1"></div>
          <div className="h-2 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full"></div>
      </div>
    </div>
  );

  const SkeletonMobileCard = () => (
    <div className="bg-gray-50 rounded p-2 border border-gray-200 animate-pulse">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center space-x-1">
          <div className="w-3.5 h-3.5 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-32"></div>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
        </div>
      </div>
      <div className="h-3 bg-gray-200 rounded w-40 mb-1"></div>
      <div className="flex items-center space-x-2">
        <div className="h-5 bg-gray-200 rounded w-12"></div>
        <div className="h-5 bg-gray-200 rounded w-12"></div>
      </div>
    </div>
  );

  // Helper function to determine if user is active
  // User is active only if not deleted AND has completed first login (profile setup)
  const isUserActive = (user) => {
    return !user.isDeleted && !user.isFirstLogin;
  };

  const SkeletonTableRow = () => (
    <tr className="animate-pulse">
      <td className="px-3 py-2 whitespace-nowrap text-center">
        <div className="w-3.5 h-3.5 bg-gray-200 rounded mx-auto"></div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="h-4 bg-gray-200 rounded w-32"></div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="h-4 bg-gray-200 rounded w-40"></div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="h-5 bg-gray-200 rounded w-16"></div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="h-5 bg-gray-200 rounded w-16"></div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-center">
        <div className="flex items-center justify-center space-x-1">
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
        </div>
      </td>
    </tr>
  );

  // Fetch stats on component mount (always, regardless of filters)
  useEffect(() => {
    fetchAllUsersStats();
  }, []); // Only run once on mount

  // Fetch all users when filters change (but not when page changes)
  useEffect(() => {
    if (filterRole !== 'all' || filterStatus !== 'all' || searchTerm !== '') {
      fetchAllUsersForFiltering();
    } else {
      // If no filters, use normal pagination
      if (allUsers.length > 0) {
        // Clear allUsers when switching back to pagination mode
        setAllUsers([]);
      }
      fetchUsers(currentPage);
    }
  }, [filterRole, filterStatus, searchTerm]);

  // Fetch users when page changes (only if no filters are active)
  useEffect(() => {
    if (filterRole === 'all' && filterStatus === 'all' && searchTerm === '') {
      fetchUsers(currentPage);
    }
  }, [currentPage]);

  // Apply filters and pagination to all users
  useEffect(() => {
    // If we have filters or search, use allUsers and paginate client-side
    if (allUsers.length > 0) {
      let filtered = allUsers.filter((user) =>
        (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Apply role filter
      if (filterRole !== 'all') {
        filtered = filtered.filter(user => user.role === filterRole);
      }

      // Apply status filter
      if (filterStatus !== 'all') {
        filtered = filtered.filter(user => 
          filterStatus === 'active' ? isUserActive(user) : !isUserActive(user)
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        let aValue = a[sortField] || '';
        let bValue = b[sortField] || '';
        
        if (sortField === 'name' || sortField === 'email') {
          aValue = (aValue || '').toLowerCase();
          bValue = (bValue || '').toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      setFilteredUsers(filtered);
      
      // Calculate pagination for filtered results
      const totalFilteredPages = Math.ceil(filtered.length / limit);
      setTotalPages(totalFilteredPages || 1);
      
      // Get users for current page
      const startIndex = (currentPage - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedUsers = filtered.slice(startIndex, endIndex);
      setDisplayedUsers(paginatedUsers);
    } else {
      // No filters, use server-side paginated users
      // Apply only search, sorting to the current page (if needed)
      let filtered = users.filter((user) =>
        (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Apply sorting
      filtered.sort((a, b) => {
        let aValue = a[sortField] || '';
        let bValue = b[sortField] || '';
        
        if (sortField === 'name' || sortField === 'email') {
          aValue = (aValue || '').toLowerCase();
          bValue = (bValue || '').toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      setFilteredUsers(filtered);
      setDisplayedUsers(filtered);
      // totalPages is already set from API response
    }
    
  }, [allUsers, users, searchTerm, filterRole, filterStatus, sortField, sortDirection, currentPage, limit]);

  const fetchUsers = async (page) => {
    setLoading(true); // Show the loader when the API request starts
    try {
      const response = await axios.get(`/api/users?page=${page}&limit=${limit}`);
      // Always ensure we have arrays, even if the response structure is unexpected
      const usersData = response.data?.users || [];
      setUsers(usersData);
      setAllUsers([]); // Clear allUsers when using pagination
      setTotalPages(response.data?.totalPages || 1);
      
      // Fetch all users for accurate stats
      await fetchAllUsersStats();
    } catch (error) {
      console.error("Error fetching users:", error);
      // Set empty arrays on error to prevent undefined errors
      setUsers([]);
      setAllUsers([]);
      setFilteredUsers([]);
      setDisplayedUsers([]);
      setTotalPages(1);
    } finally {
      setLoading(false); // Hide the loader after the request is finished
    }
  };

  const fetchAllUsersForFiltering = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/users?page=1&limit=10000`); // Large limit to get all users
      const allUsersData = response.data?.users || [];
      setAllUsers(allUsersData);
      setUsers([]); // Clear paginated users when using all users
      setCurrentPage(1); // Reset to first page when filtering
      
      // Always fetch stats to show total counts (not filtered)
      await fetchAllUsersStats();
    } catch (error) {
      console.error("Error fetching all users for filtering:", error);
      setAllUsers([]);
      setFilteredUsers([]);
      setDisplayedUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsersStats = async () => {
    try {
      // Fetch all users without pagination for accurate stats
      const response = await axios.get(`/api/users?page=1&limit=1000`); // Large limit to get all users
      const allUsers = response.data?.users || [];

      let totalUsers = response.data?.totalItems || allUsers.length;
      let activeUsers = allUsers.filter(user => isUserActive(user)).length;
      let companyAdmins = allUsers.filter(user => user.role === 'companyAdmin').length;
      const regularUsers = allUsers.filter(user => user.role === 'user').length;

      // Include the logged-in user in stats if they're a company admin and not already counted
      if (loggedInUserRole === 'companyAdmin') {
        // Check if the logged-in user is already in the fetched users list
        const loggedInUserInList = allUsers.some(user => user.role === 'companyAdmin');
        // If not found in the list, add 1 to company admins count
        // Note: We can't be sure if the logged-in user is in the list, so we add them to be safe
        companyAdmins += 1;
        // Also add 1 to total users to include the admin user
        totalUsers += 1;
        // Add 1 to active users since company admin is always considered active
        activeUsers += 1;
      }

      setStats({
        totalUsers,
        activeUsers,
        companyAdmins,
        regularUsers
      });
    } catch (error) {
      console.error("Error fetching all users stats:", error);
      // Set default stats on error, but still account for logged-in user
      const defaultStats = {
        totalUsers: 0,
        activeUsers: 0,
        companyAdmins: 0,
        regularUsers: 0
      };

      // Include logged-in user in default stats
      if (loggedInUserRole === 'companyAdmin') {
        defaultStats.companyAdmins = 1;
        defaultStats.totalUsers = 1; // Include the admin user in total count
      }

      setStats(defaultStats);
    }
  };

  const handleEdit = (id, currentName) => {
    setEditUserId(id);
    setEditName(currentName);
  };

  const handleNameChange = (e) => {
    setEditName(e.target.value);
  };

  const handleEditSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.put(`/api/users/${editUserId}`, { name: editName });
      if (res.data.success) {
        fetchUsers(currentPage);
        await fetchAllUsersStats(); // Refresh stats after edit
        setEditUserId(null);
        setEditName("");
        toastr.success(t('users.userUpdated'));
      } else {
        toastr.error(res.data.message);
      }
    } catch (error) {
      console.error("Error updating user name:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to manually activate a user (set isFirstLogin to false)
  const handleActivateUser = async (userId, userEmail) => {
    if (!window.confirm(t('users.activateMessage', { email: userEmail }))) {
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.put(`/api/users/${userId}`, { 
        name: users.find(u => u.id === userId)?.name || displayedUsers.find(u => u.id === userId)?.name,
        isFirstLogin: false 
      });
      if (res.data.success) {
        fetchUsers(currentPage);
        await fetchAllUsersStats();
        toastr.success(t('users.userActivated'));
      } else {
        toastr.error(res.data.message);
      }
    } catch (error) {
      console.error("Error activating user:", error);
      toastr.error(t('users.failedToActivateUser'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditBlur = async () => {
    if (editUserId && editName.trim() !== "") {
      await handleEditSubmit();
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('users.deleteConfirm'))) {
      setLoading(true);
      try {
        const res = await axios.delete(`/api/users/${id}`);
        if (res.data.success) {
          fetchUsers(currentPage);
          await fetchAllUsersStats(); // Refresh stats after delete
          toastr.success(t('users.userDeleted'));
        } else {
          toastr.error(res.data.message);
        }
      } catch (error) {
        console.error("Error deleting user:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddUser = async () => {
    // Validate required fields on frontend
    if (!newUser.name || !newUser.email || !newUser.role) {
      toastr.error(t('users.fillAllRequiredFields'));
      return;
    }
    
    setLoadingAddUser(true);
    try {
      const res = await axios.post("/api/users", newUser);
      if (res.data.success) {
        fetchUsers(currentPage);
        await fetchAllUsersStats(); // Refresh stats after adding user
        setShowModal(false);
        setNewUser({ 
          name: "", 
          email: "", 
          role: loggedInUserRole === "companyAdmin" ? "user" : "companyAdmin", 
          password: "" 
        });
        toastr.success(res.data.message || t('users.userAddedSuccessfully'));
      } else {
        toastr.error(res.data.message || t('users.failedToAddUser'));
      }
    } catch (error) {
      console.error("Error adding new user:", error);
      const errorMessage = error.response?.data?.message || error.message || t('users.errorAddingUser');
      toastr.error(errorMessage);
    } finally {
      setLoadingAddUser(false);
    }
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page after search
    // Filtering will be handled by useEffect
  };

  const handleSort = (field) => {
    const newSortField = field;
    const newSortDirection = sortField === field ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    
    setSortField(newSortField);
    setSortDirection(newSortDirection);
    setCurrentPage(1); // Reset to first page after sorting
    // Filtering and sorting will be handled by useEffect
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === (displayedUsers?.length || 0)) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers((displayedUsers || []).map(user => user.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return;
    
    if (window.confirm(t('users.deleteMultipleConfirm', { count: selectedUsers.length }))) {
      setLoading(true);
      try {
        await Promise.all(selectedUsers.map(id => axios.delete(`/api/users/${id}`)));
        
        // Refresh the current view with all users after bulk delete
        const response = await axios.get(`/api/users?page=1&limit=10000`);
        const allUsers = response.data.users || [];
        
        // Apply the same filters as the current view
        let filtered = allUsers.filter((user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Apply role filter
        if (filterRole !== 'all') {
          filtered = filtered.filter(user => user.role === filterRole);
        }

        // Apply status filter
        if (filterStatus !== 'all') {
          filtered = filtered.filter(user => 
            filterStatus === 'active' ? !user.isDeleted : user.isDeleted
          );
        }

        // Apply sorting
        filtered.sort((a, b) => {
          let aValue = a[sortField];
          let bValue = b[sortField];
          
          if (sortField === 'name' || sortField === 'email') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }
          
          if (sortDirection === 'asc') {
            return aValue > bValue ? 1 : -1;
          } else {
            return aValue < bValue ? 1 : -1;
          }
        });

        setFilteredUsers(filtered);
        setCurrentPage(1); // Reset to first page after bulk delete
        await fetchAllUsersStats(); // Refresh stats after bulk delete
        setSelectedUsers([]);
        toastr.success(t('users.usersDeletedSuccessfully', { count: selectedUsers.length }));
      } catch (error) {
        console.error("Error deleting users:", error);
        toastr.error(t('users.errorDeletingUsers'));
        setFilteredUsers([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const exportUsers = async () => {
    try {
      // Fetch all users for export
      const response = await axios.get(`/api/users?page=1&limit=10000`); // Large limit to get all users
      const allUsers = response.data.users || [];
      
      // Apply the same filters as the current view
      let exportUsers = allUsers.filter((user) =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Apply role filter
      if (filterRole !== 'all') {
        exportUsers = exportUsers.filter(user => user.role === filterRole);
      }

      // Apply status filter
      if (filterStatus !== 'all') {
        exportUsers = exportUsers.filter(user => 
          filterStatus === 'active' ? isUserActive(user) : !isUserActive(user)
        );
      }

      // Apply sorting
      exportUsers.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        if (sortField === 'name' || sortField === 'email') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      const csvContent = [
        [t('users.name'), t('users.email'), t('users.role'), t('users.status'), t('users.assignedSustainabilityDimension'), t('users.createdAt')],
        ...exportUsers.map(user => [
          user.name,
          user.email,
          user.role === 'companyAdmin' ? t('users.admin') : t('users.user'),
          isUserActive(user) ? t('users.active') : t('users.inactive'),
          user.assignedSustainabilityDimension || t('users.notAssigned'),
          new Date(user.createdAt || Date.now()).toLocaleDateString()
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting users:", error);
      toastr.error(t('users.errorExportingUsers'));
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination with ellipsis
      if (currentPage <= 3) {
        // Show first 3 pages, ellipsis, then last page
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Show first page, ellipsis, then last 3 pages
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        // Show first page, ellipsis, current page and neighbors, ellipsis, last page
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  // Reset the search term when the modal is opened
  const openAddNewUserModal = () => {
    setSearchTerm("");  // Reset search term
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans antialiased">
      {/* Header - Responsive */}
      <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-lg relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow">
                <FaUsers className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{t('users.userManagement')}</h1>
                <p className="text-green-100 text-xs sm:text-sm">{t('users.manageUsers')}</p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs text-green-100">{t('users.lastUpdated')}: {new Date().toLocaleDateString()}</p>
              <p className="text-sm text-lime-300 font-bold">{stats.totalUsers} {t('users.totalUsers')}</p>
              <p className="text-xs text-green-200">{t('users.showingFiltered', { count: filteredUsers?.length || 0 })}</p>
            </div>
          </div>
        </div>
      </div>
  
      {/* Compact Stats Cards - Responsive */}
      <div className="max-w-full mx-auto px-3 sm:px-4 py-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {loading ? (
            <>
              <SkeletonStatsCard />
              <SkeletonStatsCard />
              <SkeletonStatsCard />
              <SkeletonStatsCard />
            </>
          ) : (
            <>
              {/* Total Users Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('users.totalUsers')}</p>
                    <p className="text-lg sm:text-xl font-bold text-blue-600">{stats.totalUsers}</p>
                    <div className="text-xs text-gray-500">{t('users.allRegistered')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <FaUsers className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                </div>
              </div>
      
              {/* Active Users Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('dashboard.activeUsers')}</p>
                    <p className="text-lg sm:text-xl font-bold text-green-600">{stats.activeUsers}</p>
                    <div className="text-xs text-gray-500">{t('dashboard.currentlyActive')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <FaUserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                </div>
              </div>
      
              {/* Company Admins Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('users.companyAdmins')}</p>
                    <p className="text-lg sm:text-xl font-bold text-purple-600">{stats.companyAdmins}</p>
                    <div className="text-xs text-gray-500">{t('users.adminUsers')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <FaUserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                </div>
              </div>
      
              {/* Regular Users Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('users.regularUsers')}</p>
                    <p className="text-lg sm:text-xl font-bold text-orange-600">{stats.regularUsers}</p>
                    <div className="text-xs text-gray-500">{t('users.standardUsers')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <FaUsers className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
  
        {/* Compact Search and Add User Section - Responsive */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-3 mb-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="flex flex-col md:flex-row items-center gap-3 flex-1 w-full">
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  placeholder={t('users.searchUsers')}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-100 text-sm"
                  value={searchTerm}
                  onChange={handleSearch}
                  autoComplete="off"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
                    showFilters 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FaFilter className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('users.filters')}</span>
                </button>
                
                <button
                  onClick={exportUsers}
                  className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 text-sm"
                >
                  <FaDownload className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('users.export')}</span>
                </button>
              </div>
            </div>
            
            <button
              className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm w-full md:w-auto justify-center"
              onClick={openAddNewUserModal}
            >
              <FaPlus className="w-3 h-3" />
{t('users.addUser')}
            </button>
          </div>
  
          {/* Advanced Filters - Responsive */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('users.filterByRole')}</label>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  >
                    <option value="all">{t('users.allRoles')}</option>
                    <option value="user">{t('users.user')}</option>
                    <option value="companyAdmin">{t('users.companyAdmin')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('users.filterByStatus')}</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  >
                    <option value="all">{t('users.allStatus')}</option>
                    <option value="active">{t('users.active')}</option>
                    <option value="inactive">{t('users.inactive')}</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setFilterRole('all');
                      setFilterStatus('all');
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    className="w-full px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
                  >
                    {t('users.clearFilters')}
                  </button>
                </div>
              </div>
            </div>
          )}
  
          {/* Bulk Actions - Responsive */}
          {selectedUsers.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {selectedUsers.length} {t('users.usersSelected')}
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    className="px-2 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-1 text-sm"
                  >
                    <FaTrashAlt className="w-3 h-3" />
                    <span className="hidden sm:inline">{t('users.deleteSelected')}</span>
                  </button>
                </div>
                <button
                  onClick={() => setSelectedUsers([])}
                  className="px-2 py-1.5 bg-white text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs"
                >
                  {t('users.clearSelection')}
                </button>
              </div>
            </div>
          )}
        </div>
  
        {/* Compact Users Table - Mobile Card Layout */}
        <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-700 px-3 sm:px-4 py-3">
            <div className="flex items-center space-x-2">
              <FaUsers className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              <div>
                <h2 className="text-white text-sm sm:text-base font-semibold">
                  {t('users.usersList')}
                </h2>
                <p className="text-green-100 text-xs">
                  {t('users.manageAndMonitor')}
                </p>
              </div>
            </div>
          </div>
  
          {/* Mobile Card Layout */}
          <div className="lg:hidden">
            {loading ? (
              <div className="p-3 space-y-2">
                <SkeletonMobileCard />
                <SkeletonMobileCard />
                <SkeletonMobileCard />
                <SkeletonMobileCard />
                <SkeletonMobileCard />
              </div>
            ) : displayedUsers && displayedUsers.length > 0 ? (
              <div className="p-3 space-y-2">
                {displayedUsers.map((user) => (
                  <div key={user.id} className="bg-gray-50 rounded p-2 border border-gray-200">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center space-x-1">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                          className="w-3.5 h-3.5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                        />
                        <div className="text-xs font-semibold text-gray-900">{user.name}</div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                          onClick={() => handleDelete(user.id)}
                          disabled={loading}
                        >
                          <FaTrashAlt className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </div>
  
  
                    <div className="text-xs text-gray-700 mb-1">{user.email}</div>
                    
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'companyAdmin' 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-blue-500 text-white'
                      }`}>
                        {user.role === 'companyAdmin' ? t('users.admin') : t('users.user')}
                      </span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        isUserActive(user)
                          ? 'bg-green-500 text-white' 
                          : 'bg-red-500 text-white'
                      }`}>
                        {isUserActive(user) ? t('users.active') : t('users.inactive')}
                      </span>
                    </div>
                    
                    <div className="text-xs text-gray-600">
                      <span className="font-semibold">{t('users.sustainabilityDimension')}: </span>
                      <span>{user.assignedSustainabilityDimension || t('users.notAssigned')}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mb-3 mx-auto">
                  <FaUserTimes className="w-6 h-6 text-white" />
                </div>
                <p className="text-gray-600 text-sm font-semibold mb-1">{t('users.noUsersFound')}</p>
                <p className="text-gray-400 text-xs">{t('users.tryAdjustingSearch')}</p>
              </div>
            )}
          </div>
  
          {/* Desktop Table - Responsive with horizontal scroll */}
          <div className="hidden lg:block">
            {loading ? (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">{t('users.select')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('users.name')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('users.email')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('users.role')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('users.status')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('users.assignedSustainabilityDimension')}</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">{t('users.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <SkeletonTableRow />
                    <SkeletonTableRow />
                    <SkeletonTableRow />
                    <SkeletonTableRow />
                    <SkeletonTableRow />
                  </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">
                        <input
                          type="checkbox"
                          checked={selectedUsers.length === (displayedUsers?.length || 0) && (displayedUsers?.length || 0) > 0}
                          onChange={handleSelectAll}
                          className="w-3.5 h-3.5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                        />
                      </th>
                      <th 
                        className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-200"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          {t('users.name')}
                          {sortField === 'name' ? (
                            sortDirection === 'asc' ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />
                          ) : (
                            <FaSort className="w-3 h-3 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-200"
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center gap-1">
                          {t('users.email')}
                          {sortField === 'email' ? (
                            sortDirection === 'asc' ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />
                          ) : (
                            <FaSort className="w-3 h-3 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('users.role')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('users.status')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('users.assignedSustainabilityDimension')}</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">{t('users.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayedUsers && displayedUsers.length > 0 ? (
                      displayedUsers.map((user) => (
                        <tr
                          key={user.id}
                          className={`hover:bg-green-50 ${
                            selectedUsers.includes(user.id) ? 'bg-green-100' : ''
                          }`}
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-center">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => handleSelectUser(user.id)}
                              className="w-3.5 h-3.5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm text-gray-700">{user.email}</div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'companyAdmin' 
                                ? 'bg-orange-500 text-white' 
                                : 'bg-blue-500 text-white'
                            }`}>
                              {user.role === 'companyAdmin' ? t('users.admin') : t('users.user')}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              isUserActive(user)
                                ? 'bg-green-500 text-white' 
                                : 'bg-red-500 text-white'
                            }`}>
                              {isUserActive(user) ? t('users.active') : t('users.inactive')}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm text-gray-700">
                              {user.assignedSustainabilityDimension || t('users.notAssigned')}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                                onClick={() => handleDelete(user.id)}
                                disabled={loading}
                              >
                                <FaTrashAlt className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center">
                          <div className="flex flex-col items-center">
                            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mb-2">
                              <FaUserTimes className="w-6 h-6 text-white" />
                            </div>
                            <p className="text-gray-600 text-sm font-semibold mb-1">No users found</p>
                            <p className="text-gray-400 text-xs">Try adjusting your search criteria or add new users</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Compact Pagination - Responsive */}
          <div className="bg-gray-50 px-3 sm:px-4 py-2 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-xs text-gray-700">
                Page <span className="font-bold text-green-600">{currentPage}</span> of <span className="font-bold text-green-600">{totalPages}</span>
                <span className="ml-1 text-gray-500">({filteredUsers?.length || 0} total, showing {displayedUsers?.length || 0} on this page)</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-xs"
                  disabled={currentPage === 1 || loading}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </button>

                {/* Page Number Buttons */}
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2 py-1 text-xs text-gray-500">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`px-2 py-1 rounded text-xs ${
                        page === currentPage
                          ? 'bg-green-600 text-white font-semibold'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                      disabled={loading}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  )
                ))}

                <button
                  className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-xs"
                  disabled={currentPage === totalPages || loading}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      {/* Compact Add New User Modal - Responsive */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-lg shadow max-w-md w-full border border-gray-200">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-3 sm:px-4 py-3">
              <h3 className="text-white text-sm sm:text-base font-semibold flex items-center">
                <FaPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                Add New User
              </h3>
              <p className="text-green-100 text-xs">Create a new user account</p>
            </div>
            
            <div className="p-3 space-y-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={newUser.name}
                  onChange={handleNewUserChange}
                  placeholder="Enter full name"
                  className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={newUser.email}
                  onChange={handleNewUserChange}
                  placeholder="Enter email address"
                  className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Role</label>
                <select
                  name="role"
                  value={newUser.role}
                  onChange={handleNewUserChange}
                  className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                >
                  {loggedInUserRole === "superAdmin" ? (
                    <option value="companyAdmin">Company Admin</option>
                  ) : (
                    <option value="user">User</option>
                  )}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Password <span className="text-gray-400 text-xs">(Optional - user will receive reset link)</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={newUser.password}
                    onChange={handleNewUserChange}
                    placeholder="Enter password (optional)"
                    autoComplete="new-password"
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 pr-10 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <FaEye className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-3 py-2 flex justify-end space-x-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-sm"
                disabled={loadingAddUser}
              >
                {loadingAddUser ? "Adding..." : "Add User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTable;
